import { useContext, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { INIT_TIMEKEEPING_OVERVIEW } from "../constants/timekeepingOverviewConsts";
import { useFormik } from "formik";
import * as Yup from "yup";
import useMultiLanguage from "../../../hook/useMultiLanguage";
import { GroupButton } from "../../components/GroupButton";
import { KTSVG } from "../../../../_metronic/helpers";
import AppContext from "../../../AppContext";
import TableTimesheetsSynthetic from "./TableTimesheetsSynthetic";
import TimekeepingSyntheticDialog from "./TimekeepingSyntheticDialog";
import moment from "moment";
import { FORMAT_DATE, INIT_SEARCH_OBJECT, RESPONSE_STATUS_CODE } from "../../constants/moduleConsts";
import { searchTimekeepingSynthetic } from "../../timesheet/services/timesheetServices";
import { toast } from "react-toastify";
import { RangeDatePicker } from "../../components/input/RangeDatePicker";
import InputSearch from "../../components/input/InputSearch";
import DialogPrintTimekeepingSynthetic from "./DialogPrintTimekeepingSynthetic";

const TimekeepingSynthetic = () => {
    const { lang } = useMultiLanguage();
    const { setPageLoading } = useContext(AppContext);

    const [initDataTable, setInitDataTable] = useState<any[]>([]);
    const [initPageable, setInitPageable] = useState<any>({
        totalElements: 0,
        totalPages:0,
        numberOfElements: 0,
    });
    const [openEmployeeListDialog, setOpenEmployeeListDialog] = useState<{
        open: boolean;
        data: any;
    }>({
        open: false,
        data: null
    });
    const [searchObject, setSearchObject] = useState<any>({
        ...INIT_SEARCH_OBJECT
    });
    const [openDialogPrint, setOpenDialogPrint] = useState<any>({
        open: false,
        dataSearchPrint: null
    });

    useEffect(() => {
        if(searchObject?.pageIndex && searchObject?.pageSize) {
          handleSearch(searchObject);
        }
    }, [searchObject?.pageIndex, searchObject?.pageSize])

    const handleSearch = async ({ pageIndex, pageSize }: any) => {
        try {
            setPageLoading(true);
            let { fromDate, toDate, keyword } = formik.values;
            let searchObject = {
                pageSize,
                pageIndex,
                fromDate: fromDate ? moment(fromDate).format(FORMAT_DATE.YYYY_MM_DD_INPUT) : null,
                toDate: toDate ? moment(toDate).format(FORMAT_DATE.YYYY_MM_DD_INPUT) : null,
                keyword: keyword
            };
            setOpenDialogPrint({
                open: false,
                dataSearchPrint: searchObject
            });
            const { data } = await searchTimekeepingSynthetic(searchObject);
            if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
                setInitDataTable(data?.data?.content || [])
                setInitPageable(data?.data?.pageable || {
                    totalElements: 0,
                    totalPages:0,
                    numberOfElements: 0,
                })
            } else toast.warning(data?.message);
        } catch (error) {
            toast.error(lang("GENERAL.ERROR"));
        } finally {
            setPageLoading(false);
        }
    };

    const validationSchema = Yup.object().shape({
        fromDate: Yup.date()
            .required(lang("VALIDATION.REQUIRE"))
            .nullable(),
        toDate: Yup.date()
            .required(lang("VALIDATION.REQUIRE"))
            .nullable()
            .test("is-greater-or-equal", lang("VALIDATION.MINDATE"), function (value) {
                const { fromDate } = this.parent;
                return !fromDate || !value || moment(value).isSameOrAfter(moment(fromDate), "day");
            }),
    });

    const formik = useFormik({
        initialValues: {
            keyword: null,
            fromDate: moment(),
            toDate: moment(),
        },
        validationSchema: validationSchema,
        onSubmit: () => handleSearch(searchObject)
    });


    return (
        <Form onSubmit={formik.handleSubmit}>
            <Row className="spaces relative z-index-4">
                <Col xs={6} sm={5} md={5} lg={4} xl={4}>
                    <RangeDatePicker
                        label={lang("GENERAL.TIME")}
                        startDateName="fromDate"
                        endDateName="toDate"
                        isView={false}
                        isRequired
                        handleChange={formik.handleChange}
                        value={formik.values}
                        setFieldValue={formik.setFieldValue}
                        touch={formik.touched}
                        errors={formik.errors}
                    />
                </Col>

                <Col xs={4} sm={4} md={4} lg={4} xl={4}>
                    <div className="spaces mr-10 flex-1 mt-21">
                        <InputSearch
                            className="spaces border-3px"
                            value={formik.values?.keyword ?? ''}
                            placeholder="Tìm kiếm theo tên phòng ban"
                            handleSearch={() => handleSearch({ pageIndex: 1 })}
                            handleChange={(e: any) => formik.setFieldValue('keyword', e.target.value)}
                            isEnter
                        />
                    </div>
                </Col>

                <Col xs={2} className="spaces flex flex-start mt-21">
                    <GroupButton typeButton="submit" className='spaces pr-10'>
                        <i className="bi bi-funnel spaces flex fs-18 text-white mr-6"></i>
                        {lang("GENERAL.FILTER")}
                    </GroupButton>
                </Col>
            </Row>
            <Row>
                <Col xs={12} className={""}>
                    <div className="relative pt-5 mb-3">
                        <p className='fw-bold text-uppercase fs-1 text-center m-0'>bảng tổng hợp nhân sự</p>
                        <p className='fw-bold fs-4 text-center m-0'>
                            {moment(formik.values.fromDate).isSame(formik.values.toDate, 'day') ?
                                `Ngày ${moment(formik.values.fromDate).format("DD")} tháng ${moment(formik.values.fromDate).format("MM")} năm ${moment(formik.values.fromDate).format("YYYY")}`
                                :
                                `Từ ngày ${moment(formik.values.fromDate).format(FORMAT_DATE.DD_MM_YYYY)} đến ngày ${moment(formik.values.toDate).format(FORMAT_DATE.DD_MM_YYYY)}`
                            }
                        </p>
                        <div className="position-absolute bottom-0 end-0 d-flex spaces gap-2">
                            <GroupButton handleEvent={() => setOpenDialogPrint((prev: any) => {
                                return {
                                    ...prev,
                                    open: true
                                }
                            })}>
                                {lang("BTN.PRINT")}
                            </GroupButton>
                        </div>
                    </div>
                    <TableTimesheetsSynthetic
                        data={[...initDataTable]}
                        pageable={initPageable}
                        searchObject={searchObject}
                        setSearchObject={setSearchObject}
                        handleClick={(data: any) => {
                            const { fromDate, toDate } = formik.values;
                            setOpenEmployeeListDialog({
                                open: true,
                                data: {
                                    ...data,
                                    fromDate,
                                    toDate
                                }
                            })
                        }}
                    />
                </Col>
            </Row>
            {openEmployeeListDialog.open &&
                <TimekeepingSyntheticDialog
                    open={openEmployeeListDialog.open}
                    handleClose={() => setOpenEmployeeListDialog({
                        open: false,
                        data: null,
                    })}
                    data={openEmployeeListDialog.data}
                />
            }
            {openDialogPrint.open &&
                <DialogPrintTimekeepingSynthetic
                    open={openDialogPrint}
                    dateSearch={openDialogPrint.dataSearchPrint}
                    data={[...initDataTable]}
                    handleClose={() => setOpenDialogPrint({
                        open: false,
                        dataSearchPrint: openDialogPrint.dataSearchPrint
                    })}
                />
            }
        </Form >
    )
};

export default TimekeepingSynthetic;